import React from 'react';
import { history } from '@umijs/max';
import type { Settings as LayoutSettings } from '@ant-design/pro-layout';
import type { CurrentUser } from './interfaces/user';
import { isRefreshValid, isValid, outLogin } from './services/login.service';
import { queryCurrent } from './services/user.service';
import { getMetricSections } from './services/metrics.service';
import type { MetricSection } from './interfaces/metrics';
import defaultSettings from '../config/defaultSettings';
import { ConfigProvider } from 'antd';
import ErrorBoundary from './components/ErrorBoundary';
import { registerGlobalErrorHandlers } from './utils/globalErrorHandlers';

registerGlobalErrorHandlers();
import type { AxiosError } from 'axios';

const anonymousPages = [
  '/ingresar',
  '/olvide-contraseña',
  '/restablecer-contraseña',
  '/primer-acceso',
  '/terminos-y-condiciones',
];

export async function getInitialState(): Promise<{
  currentUser?: CurrentUser | undefined;
  metricSections?: MetricSection[];
  settings?: LayoutSettings;
  refresh?: () => Promise<CurrentUser | undefined>;
}> {
  const isAnonymous = anonymousPages.includes(history.location.pathname);

  if (isAnonymous) {
    const access_valid = isValid();
    const refresh_valid = isRefreshValid();
    if (access_valid && refresh_valid) {
      window.location.href = '/inicio';
      return { settings: defaultSettings };
    } else {
      return { settings: defaultSettings };
    }
  } else {
    const searchParams = new URLSearchParams(window.location.search);
    const access_params = searchParams.get('access_token');
    const refresh_params = searchParams.get('refresh_token');
    if (access_params && refresh_params) {
      localStorage.setItem('token', access_params);
      localStorage.setItem('refresh', refresh_params);
    }
  }

  try {
    const currentUser = await queryCurrent();
    if (isAnonymous && currentUser) {
      window.location.href = '/inicio';
      return { settings: defaultSettings };
    }

    // El backend es la única autoridad para habilitar Métricas. Un fallo de esta
    // consulta no debe impedir el ingreso a la aplicación ni habilitar enlaces.
    const metricSections = await getMetricSections()
      .then(({ data }) => {
        const sections = Array.isArray(data) ? data : data.results;
        return [...sections].sort((a, b) => a.order - b.order);
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 401) throw error;
        return [];
      });

    return {
      currentUser,
      metricSections,
      settings: defaultSettings,
    };
  } catch (error) {
    if (!isAnonymous) {
      outLogin();
      window.location.href = '/ingresar';
    }

    return {
      settings: defaultSettings,
    };
  }
}



export function rootContainer(container: React.ReactNode) {
  return (
    <ConfigProvider
      theme={{
        inherit: true,
        token: {
          colorLink: defaultSettings.colorPrimary,
          colorPrimaryBg: defaultSettings.colorPrimary,
          controlItemBgActive: defaultSettings.colorPrimary,
          colorPrimary: defaultSettings.colorPrimary,
          colorBgContainer: '#f0f2f5',
          colorBgBase: '#f0f2f5',
        },
        components: {
          Button: {
            primaryShadow: ''
          },
          Alert: {
            colorInfo: defaultSettings.colorPrimary,
            colorInfoBg: `${defaultSettings.colorPrimary}11`,
            colorInfoBorder: `${defaultSettings.colorPrimary}33`,
          },
          DatePicker: {
            activeBorderColor: defaultSettings.colorPrimary,
            hoverBorderColor: defaultSettings.colorPrimary,
            colorPrimary: defaultSettings.colorPrimary,
            cellActiveWithRangeBg: `${defaultSettings.colorPrimary}22`,
            colorText: '#333',
          },
          Table: {
            rowSelectedBg:`${defaultSettings.colorPrimary}22`
          }
        },
      }}
    >
      <ErrorBoundary>{container}</ErrorBoundary>
    </ConfigProvider>
  );
}
